import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, LogIn, ArrowLeft, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { supabase, formatSupabaseError } from "@/lib/supabase";

export default function AdminLogin() {
  const { user, signIn, loading } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | forgot | forgot-sent
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/admin" replace />;

  const onLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await signIn(email, password);
      toast.success("Signed in successfully");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatSupabaseError(err) || "Invalid email or password");
    } finally {
      setSubmitting(false);
    }
  };

  const onForgot = async (e) => {
    e.preventDefault();
    if (!supabase) {
      toast.error("Auth is not configured.");
      return;
    }
    setSubmitting(true);
    try {
      const redirectTo = `${window.location.origin}/admin/reset-password`;
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
      if (error) throw error;
      setMode("forgot-sent");
    } catch (err) {
      toast.error(formatSupabaseError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-[100svh] bg-cream flex items-center justify-center p-5">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo /></div>

        {mode === "login" && (
          <form onSubmit={onLogin} className="rounded-3xl bg-white border border-ink/5 shadow-md p-8 sm:p-10" data-testid="admin-login-form">
            <div className="flex items-center gap-2.5 text-terracotta">
              <Lock size={18} /><span className="text-xs uppercase tracking-[0.18em] font-semibold">Admin</span>
            </div>
            <h1 className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-ink">Sign in</h1>
            <p className="mt-2 text-sm text-ink/60">Manage services, reviews, waitlist, and contact info.</p>
            <div className="mt-8 space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jonna@dyslexiatutor.com" className="rounded-xl h-11" data-testid="login-email" />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <button
                    type="button"
                    onClick={() => setMode("forgot")}
                    className="text-xs text-terracotta hover:text-terracotta-600 link-underline"
                    data-testid="login-forgot-link"
                  >
                    Forgot password?
                  </button>
                </div>
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="rounded-xl h-11" data-testid="login-password" />
              </div>
            </div>
            <Button type="submit" disabled={submitting} className="mt-8 w-full rounded-full bg-terracotta hover:bg-terracotta-600 text-white h-12 shadow-md" data-testid="login-submit">
              {submitting ? "Signing in..." : (<><LogIn size={16} className="mr-1.5" /> Sign In</>)}
            </Button>
          </form>
        )}

        {mode === "forgot" && (
          <form onSubmit={onForgot} className="rounded-3xl bg-white border border-ink/5 shadow-md p-8 sm:p-10" data-testid="admin-forgot-form">
            <button type="button" onClick={() => setMode("login")} className="flex items-center gap-1.5 text-sm text-ink/60 hover:text-ink mb-5" data-testid="forgot-back">
              <ArrowLeft size={14} /> Back to sign in
            </button>
            <div className="flex items-center gap-2.5 text-terracotta">
              <Mail size={18} /><span className="text-xs uppercase tracking-[0.18em] font-semibold">Reset</span>
            </div>
            <h1 className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-ink">Forgot your password?</h1>
            <p className="mt-2 text-sm text-ink/60">Enter your email and we'll send you a link to set a new one.</p>
            <div className="mt-8 space-y-1.5">
              <Label htmlFor="forgot-email">Email</Label>
              <Input id="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jonna@dyslexiatutor.com" className="rounded-xl h-11" data-testid="forgot-email" />
            </div>
            <Button type="submit" disabled={submitting} className="mt-8 w-full rounded-full bg-terracotta hover:bg-terracotta-600 text-white h-12 shadow-md" data-testid="forgot-submit">
              {submitting ? "Sending..." : "Send reset link"}
            </Button>
          </form>
        )}

        {mode === "forgot-sent" && (
          <div className="rounded-3xl bg-sage-100 border border-sage/30 shadow-md p-8 sm:p-10 text-center" data-testid="admin-forgot-sent">
            <div className="mx-auto h-12 w-12 rounded-full bg-sage text-white flex items-center justify-center"><Mail size={22} /></div>
            <h2 className="mt-5 font-display text-2xl font-semibold text-ink">Check your inbox</h2>
            <p className="mt-3 text-sm text-ink/70">
              If <span className="font-medium text-ink">{email}</span> matches an account, a password reset link is on its way.
              It expires in 1 hour.
            </p>
            <Button
              onClick={() => { setMode("login"); setEmail(""); setPassword(""); }}
              variant="outline"
              className="mt-7 rounded-full border-2 border-ink/80 hover:bg-ink hover:text-white"
              data-testid="forgot-sent-back"
            >
              Back to sign in
            </Button>
          </div>
        )}
      </div>
    </main>
  );
}
